/**
 * Utilization Optimizer - Execution phase for utilization optimization
 *
 * This module applies optimization changes identified by the analysis phase,
 * using proven methods from the core processing engines.
 */

import { PrincipalBasedRecalculationEngine } from '~/engine/processing/engines/cashflow-engine/principal-recalculator';
import { ProcessingFinalizer } from '~/engine/processing/engines/cashflow-engine/processing-finalizer';
import { CandidateNaming } from '~/engine/processing/shared/candidate-naming';
import { logger } from '~/services/logging';
import type { Candidate, Configuration } from '~/types';
import type { OptimizationAnalysis } from './utilization-analysis';

/**
 * Configuration for utilization optimizer
 */
export interface UtilizationOptimizerConfig {
  enableLogging?: boolean;
}

/**
 * Result of optimization execution
 */
export interface OptimizationExecutionResult {
  success: boolean;
  optimizedCandidate: Candidate;
  description: string;
  adjustmentDetails?: {
    periodIndex: number;
    paymentDate: string;
    totalPrincipalAdded: number;
    bondsModified: Array<{
      bondSeriesId: string;
      principalAdded: number;
    }>;
  };
}

export class UtilizationOptimizer {
  private config: UtilizationOptimizerConfig;

  constructor(config: UtilizationOptimizerConfig = {}) {
    this.config = {
      ...config,
    };
  }

  /**
   * Executes optimization based on analysis results
   * Note: Configuration is required for DRF calculations during finalization
   */
  executeOptimization(
    candidate: Candidate,
    analysis: OptimizationAnalysis,
    iterationNumber: number,
    configuration: Configuration
  ): OptimizationExecutionResult {
    // If analysis says we can't optimize, return original candidate
    if (!analysis.canOptimize || !analysis.adjustments) {
      return {
        success: false,
        optimizedCandidate: candidate,
        description: analysis.reason,
      };
    }

    // Step 1: Create new optimized candidate
    const optimizedCandidate = this.createOptimizedCandidate(candidate, iterationNumber);

    // Step 2: Apply principal additions to target period
    const targetPeriodIndex = analysis.targetPeriodIndex!;
    const targetCashFlow = optimizedCandidate.cashFlows[targetPeriodIndex];
    let totalPrincipalAdded = 0;
    const bondsModified: Array<{ bondSeriesId: string; principalAdded: number }> = [];

    // Apply each adjustment
    for (const adjustment of analysis.adjustments) {
      const bondPayment = targetCashFlow.bondPayments.find(
        (bp) => bp.bondSeriesId === adjustment.bondSeriesId
      );

      if (bondPayment && adjustment.principalToAdd > 0) {
        bondPayment.principal += adjustment.principalToAdd;
        totalPrincipalAdded += adjustment.principalToAdd;
        bondsModified.push({
          bondSeriesId: adjustment.bondSeriesId,
          principalAdded: adjustment.principalToAdd,
        });
      }
    }

    if (totalPrincipalAdded === 0) {
      return {
        success: false,
        optimizedCandidate: candidate,
        description: 'No principal adjustments were applied',
      };
    }

    // Step 3: Use PrincipalBasedRecalculationEngine for comprehensive recalculation
    // This properly handles the backwards pass, interest cascading with proration,
    // and ensures totalInterest is calculated AFTER futureInterestObligation is updated
    PrincipalBasedRecalculationEngine.recalculateFromPrincipal(
      optimizedCandidate,
      configuration.bondSeries
    );

    // Step 4: Universal finalization (DRF balances, tax year plan, results evaluation)
    // This uses the same 3-step wrap-up used by all other processing phases
    const finalizationResult = ProcessingFinalizer.finalizeAfterOptimization(
      optimizedCandidate,
      configuration
    );

    if (!finalizationResult.success) {
      return {
        success: false,
        optimizedCandidate: candidate,
        description: `Finalization failed: ${finalizationResult.error}`,
      };
    }

    // Add optimization history entry
    if (!optimizedCandidate.optimizationHistory) {
      optimizedCandidate.optimizationHistory = [];
    }

    const paymentDate = targetCashFlow.paymentDate;
    const formattedDate = new Date(paymentDate).toISOString().split('T')[0];

    optimizedCandidate.optimizationHistory.push({
      type: 'utilization',
      description: `Added $${totalPrincipalAdded.toLocaleString()} to ${formattedDate} to optimize DRF utilization`,
      timestamp: new Date().toISOString(),
      details: {
        principalAdded: totalPrincipalAdded,
        toDate: paymentDate,
        bondSeriesAffected: bondsModified.map((b) => b.bondSeriesId),
        drfImpact: totalPrincipalAdded,
      },
    });

    // Log the optimization
    if (this.config.enableLogging) {
      logger.L3.info(`Adding principal to optimize utilization`, {
        amount: totalPrincipalAdded,
        date: formattedDate,
      });
    }

    return {
      success: true,
      optimizedCandidate,
      description: `Added $${totalPrincipalAdded.toLocaleString()} principal to optimize utilization`,
      adjustmentDetails: {
        periodIndex: targetPeriodIndex,
        paymentDate: targetCashFlow.paymentDate,
        totalPrincipalAdded,
        bondsModified,
      },
    };
  }

  /**
   * Creates a new candidate for optimization iteration
   */
  private createOptimizedCandidate(candidate: Candidate, iteration: number): Candidate {
    // Count actual optimizations (excluding initial-shape and optimization-skipped)
    const appliedOptCount =
      candidate.optimizationHistory?.filter(
        (h) => h.type !== 'initial-shape' && h.type !== 'optimization-skipped'
      ).length || 0;
    const newOptCount = appliedOptCount + 1;

    // Use the centralized naming system: {shapeId}.{count}
    const newName = CandidateNaming.createOptimizationName(candidate.name, newOptCount);

    logger.L3.info(`Creating optimized candidate for iteration ${iteration}`);
    logger.L3.debug(`  Input: "${candidate.name}"`);
    logger.L3.debug(`  Output: "${newName}"`);
    logger.L3.debug(`  Optimization count: ${newOptCount}`);

    // Generate new unique ID
    const shapeId = CandidateNaming.getShapeId(candidate.name);

    return {
      ...candidate,
      id: `${shapeId}.${newOptCount}-${crypto.randomUUID().slice(0, 8)}`,
      name: newName,
      // Deep copy cash flows
      cashFlows: candidate.cashFlows.map((cf) => ({
        ...cf,
        bondPayments: cf.bondPayments ? cf.bondPayments.map((bp) => ({ ...bp })) : [],
      })),
      createdInPhase: 'optimization',
      isProcessed: true,
      // Copy and extend optimization history
      optimizationHistory: [...(candidate.optimizationHistory || [])],
      // Reset results to clear failure messages from the original candidate
      // The finalization process will recalculate these
      results: {
        isViable: true,
        failureMessages: [],
        totalRevenue: 0,
        totalInterest: 0,
        totalPrincipal: 0,
        revenueUtilization: 0,
        interestRatio: 0,
        efficiency: 0,
        bondSeriesResults: [],
      },
    };
  }
}
